import type {
  Accommodation,
  AccommodationStay,
  ItineraryData,
} from "../../dto/ItineraryData.dto.ts";
import type { ResolvedBooking } from "../../dto/ResolvedBooking.d.ts";
import BookedStayFallbackName from "../../_const/trip/BookedStayFallbackName.ts";

const withoutStayMetadata = (stay: AccommodationStay): Accommodation => ({
  place: stay.place,
  place_description: stay.place_description,
  geoapify_place_id: stay.geoapify_place_id,
  formatted_address: stay.formatted_address,
  city: stay.city,
  country_code: stay.country_code,
  latitude: stay.latitude,
  longitude: stay.longitude,
});

export type ScheduledAccommodation = Accommodation & Partial<AccommodationStay>;

export const getAccommodationStays = (
  itinerary: ItineraryData,
): ScheduledAccommodation[] => {
  if (itinerary.accommodations?.length) return itinerary.accommodations;

  return itinerary.where_you_will_stay ? [itinerary.where_you_will_stay] : [];
};

export const getAccommodationForDay = (
  itinerary: ItineraryData,
  day: number,
): Accommodation | undefined => {
  const stays = itinerary.accommodations;
  return (
    stays?.find((stay) =>
      stay.check_in_day <= day && day < stay.check_out_day
    ) ??
      stays?.find((stay) => stay.check_out_day === day) ??
      itinerary.where_you_will_stay
  );
};

export const preserveBookedAccommodations = (
  itinerary: ItineraryData,
  bookings: ResolvedBooking[],
): ItineraryData => {
  if (!itinerary.accommodations) return itinerary;

  const bookedStays: AccommodationStay[] = bookings.flatMap((booking) => {
    if (booking.booking_type !== "stay" || booking.covers_days.length === 0) {
      return [];
    }
    const place = booking.provider ?? booking.location ??
      BookedStayFallbackName.ACCOMMODATION;
    const existing = itinerary.accommodations?.find((stay) =>
      stay.booking_id === booking.id && stay.place === place
    );
    return [
      {
        ...existing,
        place,
        place_description: booking.notes ?? existing?.place_description ??
          "Your existing accommodation booking.",
        geoapify_place_id: existing?.geoapify_place_id ?? null,
        formatted_address: booking.location ?? existing?.formatted_address ?? null,
        city: existing?.city ?? null,
        country_code: existing?.country_code ?? null,
        latitude: existing?.latitude ?? null,
        longitude: existing?.longitude ?? null,
        check_in_day: Math.min(...booking.covers_days),
        check_out_day: Math.max(...booking.covers_days) + 1,
        booking_id: booking.id,
      },
    ];
  });

  const accommodations = [...bookedStays];
  const finalDay = Math.max(1, ...itinerary.per_day_plan.map((day) => day.day));
  let previousSource: AccommodationStay | undefined;
  let previousStay: AccommodationStay | undefined;

  for (let day = 1; day < finalDay; day += 1) {
    if (
      bookedStays.some((stay) =>
        stay.check_in_day <= day && day < stay.check_out_day
      )
    ) {
      previousSource = undefined;
      continue;
    }
    const source = itinerary.accommodations.find(
      (stay) =>
        !stay.booking_id && stay.check_in_day <= day &&
        day < stay.check_out_day,
    );
    if (!source) {
      previousSource = undefined;
      continue;
    }
    if (source === previousSource && previousStay) {
      previousStay.check_out_day = day + 1;
    } else {
      previousStay = {
        ...source,
        check_in_day: day,
        check_out_day: day + 1,
        booking_id: null,
      };
      accommodations.push(previousStay);
    }
    previousSource = source;
  }

  accommodations.sort((a, b) => a.check_in_day - b.check_in_day);
  return {
    ...itinerary,
    accommodations,
    where_you_will_stay: accommodations[0]
      ? withoutStayMetadata(accommodations[0])
      : itinerary.where_you_will_stay,
  };
};

export const validateAccommodationCoverage = (
  itinerary: ItineraryData,
  numberOfDays: number,
): string[] => {
  const stays = itinerary.accommodations ?? [];
  const violations: string[] = [];
  for (let day = 1; day < numberOfDays; day += 1) {
    const covering = stays.filter((stay) =>
      stay.check_in_day <= day && day < stay.check_out_day
    );
    if (!covering.length) {
      violations.push(
        `Missing accommodation for night ${day}. Add a suggested stay covering this night.`,
      );
    }
    if (covering.length > 1 && covering.some((stay) => !stay.booking_id)) {
      violations.push(
        `Suggested accommodation overlaps another stay on night ${day}.`,
      );
    }
  }
  for (const stay of stays) {
    if (
      !stay.place.trim() ||
      !Number.isInteger(stay.check_in_day) ||
      !Number.isInteger(stay.check_out_day) ||
      stay.check_in_day < 1 ||
      stay.check_out_day > numberOfDays ||
      stay.check_out_day <= stay.check_in_day
    ) {
      violations.push(
        `Invalid accommodation range for ${stay.place}. Use check-in inclusive and checkout exclusive day numbers within the trip.`,
      );
    }
  }
  return violations;
};
